'use client'

import { Chip, Flex, Group, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { useUpdateSearchParams } from '@/hooks/routing'

import {
  COINS_OG_PAGE_SIZE,
  COINS_PAGE_SIZE,
  CoinTab,
  defaultCoinTab,
  getCoinTabs,
  getStockTokensCoinTab,
} from '../../configs'
import { useCoinsTableStore } from '../../context'
import styles from './styles.module.css'

export default function CoinTabs({
  isOGPrice,
  tabVariant,
}: {
  isOGPrice?: boolean
  tabVariant?: 'stockTokens'
}) {
  const pageSize = isOGPrice ? COINS_OG_PAGE_SIZE : COINS_PAGE_SIZE
  let coinTabs = useMemo(
    () =>
      tabVariant === 'stockTokens'
        ? [...getCoinTabs(pageSize), getStockTokensCoinTab()]
        : getCoinTabs(pageSize),
    [pageSize, tabVariant],
  )

  if (isOGPrice) coinTabs = coinTabs.filter(tab => tab.value !== CoinTab.Favorites)

  if (tabVariant === 'stockTokens') {
    const stockTokensTabOrder = [
      CoinTab.Favorites,
      CoinTab.AllCoins,
      CoinTab.TokenizedStocks,
      CoinTab.TopGainers,
      CoinTab.TopLosers,
      CoinTab.New,
      CoinTab.MostPopular,
    ]
    coinTabs = stockTokensTabOrder
      .map(value => coinTabs.find(tab => tab.value === value))
      .filter(Boolean) as typeof coinTabs
  }
  const currentCoinTab = useCoinsTableStore(state => state.currentCoinTab)
  const updateFilters = useCoinsTableStore(state => state.updateFilters)

  const { updateSearchParams } = useUpdateSearchParams(true, false, true)

  const t = useTranslations('common.coinCategoryTypes')

  const handleTabClick = (tabValue: string) => {
    const newCurrentCoinTab = coinTabs.find(tab => tab.value === tabValue) ?? defaultCoinTab
    updateFilters({
      currentCoinTab: newCurrentCoinTab,
      currentPage: 1,
    })

    updateSearchParams([
      { name: 'tab', value: tabValue, deleteParam: tabValue === defaultCoinTab.value },
      { name: 'page', deleteParam: true },
    ])
  }

  return (
    <Flex className={styles.container}>
      <Chip.Group multiple={false} value={currentCoinTab.value} onChange={handleTabClick}>
        <Group className={styles.tabs}>
          {coinTabs.map(tab => (
            <Chip
              key={tab.value}
              icon={null}
              classNames={styles}
              variant="outline"
              value={tab.value}
              onClick={() => handleTabClick(tab.value)}
            >
              <Text variant="subtitle-emphasis">
                {tabVariant === 'stockTokens' && tab.value === CoinTab.AllCoins
                  ? t('crypto')
                  : t(tab.labelTranslationKey)}
              </Text>
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Flex>
  )
}
